import { useQuery } from "@tanstack/react-query";
import {
  Eye,
  Flame,
  Globe,
  Megaphone,
  MessageCircle,
  MousePointerClick,
  PhoneCall,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  type LucideIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { WebChats } from "./WebChats";
import {
  PERIODS,
  WEB_URL,
  fillDays,
  formatDuration,
  funnelSteps,
  heatmapUrl,
  pageLabel,
  pct,
  periodRange,
  sourceLabel,
  type HeatDevice,
  type PeriodKey,
  type WebLiveSession,
  type WebOverview,
} from "./webStats";

const rpc = async <T,>(name: string, args?: Record<string, unknown>) => {
  const { data, error } = await getSupabaseClient().rpc(name, args);
  if (error) throw error;
  return data as T;
};

/** «Web»: what happens on guladeagua.es and in its chatbot. */
export const WebPage = () => {
  const [period, setPeriod] = useState<PeriodKey>("7d");
  const range = periodRange(period);

  const overview = useQuery({
    queryKey: ["gula-web-overview", period],
    queryFn: () =>
      rpc<WebOverview>("gula_web_overview", {
        p_from: range.from.toISOString(),
        p_to: periodRange(period).to.toISOString(),
      }),
    refetchInterval: 60_000,
  });
  const live = useQuery({
    queryKey: ["gula-web-live"],
    queryFn: () => rpc<WebLiveSession[]>("gula_web_live"),
    refetchInterval: 20_000,
  });
  const heatKey = useQuery({
    queryKey: ["gula-web-heat-key"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("gula_web_settings")
        .select("heatmap_key")
        .single();
      if (error) throw error;
      return data.heatmap_key as string;
    },
    staleTime: Infinity,
  });

  const data = overview.data;
  const t = data?.totals;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-1 pb-16 pt-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Web</h1>
          <p className="text-sm text-muted-foreground">
            Lo que pasa en{" "}
            <a
              href={WEB_URL}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              guladeagua.es
            </a>{" "}
            y en su chat con IA. Cuenta todas las visitas (sin datos
            personales); las visitas del equipo no cuentan desde el navegador
            donde abras un mapa de calor.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={`${WEB_URL}/?gula_notrack=1`}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-muted-foreground underline"
            title="Abre la web en este navegador y deja de contar tus visitas"
          >
            No contar mis visitas
          </a>
          <LiveBadge count={live.data?.length ?? data?.now ?? 0} />
          <div className="flex rounded-full border p-1">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={cn(
                  "rounded-full px-3 py-1 text-sm",
                  period === p.key
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {overview.isError ? (
        <p className="rounded-2xl border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-100">
          No se han podido cargar los datos de la web.
        </p>
      ) : null}

      {t ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
            <Square
              icon={Globe}
              value={t.sessions}
              label="Visitas"
              detail={`${t.visitors} personas · ${t.pageviews} páginas`}
            />
            <Square
              icon={Megaphone}
              value={t.from_ads}
              label="Desde anuncios"
              detail={`${pct(t.from_ads, t.sessions)}% de las visitas`}
            />
            <Square
              icon={Eye}
              value={t.view_content}
              label="Ven un equipo"
              detail={`${pct(t.view_content, t.sessions)}% de las visitas`}
            />
            <Square
              icon={ShoppingCart}
              value={t.checkout}
              label="Pulsan comprar"
              detail={`${pct(t.checkout, t.sessions)}% de las visitas`}
            />
            <Square
              icon={ShoppingBag}
              value={t.purchases}
              label="Compras"
              detail={`${pct(t.purchases, t.checkout)}% de los que pulsan comprar`}
              tone={t.purchases ? "good" : "normal"}
            />
            <Square
              icon={PhoneCall}
              value={t.leads}
              label="Que me llamen"
              detail={`${pct(t.leads, t.sessions)}% de las visitas`}
              tone={t.leads ? "good" : "normal"}
            />
            <Square
              icon={MessageCircle}
              value={t.chats}
              label="Hablan con la IA"
              detail={`${pct(t.chats, t.sessions)}% de las visitas`}
            />
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
            <span>
              Tiempo medio:{" "}
              <b className="text-foreground">{formatDuration(t.avg_seconds)}</b>
            </span>
            <span>
              Se van sin hacer nada:{" "}
              <b className="text-foreground">{pct(t.bounce, t.sessions)}%</b>
            </span>
            <span>
              Desde el móvil:{" "}
              <b className="text-foreground">{pct(t.mobile, t.sessions)}%</b>
            </span>
            <span>
              Aceptan cookies:{" "}
              <b className="text-foreground">{pct(t.consent, t.sessions)}%</b>
            </span>
            {t.service ? (
              <span>
                Piden servicio técnico:{" "}
                <b className="text-foreground">{t.service}</b>
              </span>
            ) : null}
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Panel title="Visitas por día" className="lg:col-span-2">
              <DayBars days={fillDays(data.days, range.from, range.days)} />
            </Panel>
            <Panel title="Camino a la compra">
              <Funnel steps={funnelSteps(t)} />
              <p className="mt-3 text-xs text-muted-foreground">
                De cada 100 visitas, {pct(t.leads, t.sessions)} piden que les
                llamen y {pct(t.chats, t.sessions)} hablan con la IA.
              </p>
            </Panel>
          </div>

          <Panel title="De dónde vienen">
            <SourcesTable sources={data.sources} />
          </Panel>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel
              title="Páginas y mapa de calor"
              hint="El mapa se abre en la web de verdad, con los clics y hasta dónde baja la gente."
            >
              <PagesTable
                pages={data.pages}
                heatKey={heatKey.data}
                days={range.days}
              />
            </Panel>
            <Panel title="Lo que más pulsan">
              <ClicksTable clicks={data.top_clicks} />
            </Panel>
          </div>
        </>
      ) : overview.isPending ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Conversaciones con la IA" className="lg:col-span-2">
          <WebChats />
        </Panel>
        <Panel title="Ahora mismo en la web">
          <LiveList sessions={live.data ?? []} />
        </Panel>
      </div>
    </div>
  );
};

WebPage.path = "/web";

const LiveBadge = ({ count }: { count: number }) => (
  <span
    className={cn(
      "flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm",
      count
        ? "border-lime-300 bg-lime-200 text-neutral-900"
        : "text-muted-foreground",
    )}
  >
    <span
      className={cn(
        "h-2 w-2 rounded-full",
        count ? "animate-pulse bg-emerald-600" : "bg-muted-foreground/40",
      )}
    />
    {count} {count === 1 ? "persona" : "personas"} ahora
  </span>
);

const Square = ({
  icon: Icon,
  value,
  label,
  detail,
  tone = "normal",
}: {
  icon: LucideIcon;
  value: number;
  label: string;
  detail?: ReactNode;
  tone?: "normal" | "good";
}) => (
  <div
    className={cn(
      "flex min-h-32 flex-col gap-2 rounded-3xl border p-4",
      tone === "good"
        ? "border-lime-300 bg-lime-200 text-neutral-900"
        : "bg-card",
    )}
  >
    <span
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-full",
        tone === "good"
          ? "bg-neutral-900 text-lime-300"
          : "bg-muted text-muted-foreground",
      )}
    >
      <Icon className="h-4 w-4" />
    </span>
    <span className="text-3xl font-semibold tabular-nums">{value}</span>
    <span className="text-sm font-medium leading-tight">{label}</span>
    {detail ? (
      <span
        className={cn(
          "text-xs",
          tone === "good" ? "text-neutral-700" : "text-muted-foreground",
        )}
      >
        {detail}
      </span>
    ) : null}
  </div>
);

const Panel = ({
  title,
  hint,
  className,
  children,
}: {
  title: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}) => (
  <section
    className={cn(
      "flex flex-col gap-3 rounded-3xl border bg-card p-4",
      className,
    )}
  >
    <div>
      <h2 className="font-semibold">{title}</h2>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
    {children}
  </section>
);

const DayBars = ({ days }: { days: ReturnType<typeof fillDays> }) => {
  const max = Math.max(1, ...days.map((d) => d.sessions));
  if (days.length === 1) {
    const d = days[0];
    return (
      <p className="text-sm text-muted-foreground">
        Hoy: <b className="text-foreground">{d.sessions}</b> visitas,{" "}
        <b className="text-foreground">{d.checkout}</b> pulsan comprar,{" "}
        <b className="text-foreground">{d.leads}</b> piden llamada y{" "}
        <b className="text-foreground">{d.purchases}</b> compras.
      </p>
    );
  }
  return (
    <div>
      <div className="flex h-44 items-end gap-[2px]">
        {days.map((d) => (
          <div
            key={d.day}
            className="group relative flex h-full flex-1 flex-col justify-end"
            title={`${d.day}: ${d.sessions} visitas · ${d.leads} piden llamada · ${d.checkout} pulsan comprar · ${d.purchases} compras`}
          >
            <div
              className="w-full rounded-t-md bg-[#2a78d6] dark:bg-[#3987e5]"
              style={{
                height: `${(100 * d.sessions) / max}%`,
                minHeight: d.sessions ? 3 : 0,
              }}
            />
            {d.leads || d.purchases ? (
              <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-lime-500" />
            ) : null}
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
        <span>{days[0].day.slice(5).split("-").reverse().join("/")}</span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-lime-500" /> días con
          llamadas pedidas o compras
        </span>
        <span>
          {days[days.length - 1].day.slice(5).split("-").reverse().join("/")}
        </span>
      </div>
    </div>
  );
};

const Funnel = ({ steps }: { steps: ReturnType<typeof funnelSteps> }) => (
  <div className="flex flex-col gap-2">
    {steps.map((step) => (
      <div key={step.key}>
        <div className="flex justify-between text-sm">
          <span>{step.label}</span>
          <span className="tabular-nums">
            <b>{step.value}</b>{" "}
            <span className="text-muted-foreground">({step.share}%)</span>
          </span>
        </div>
        <div className="mt-1 h-2.5 rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-[#082130] dark:bg-sky-400"
            style={{ width: `${Math.max(step.value ? 2 : 0, step.share)}%` }}
          />
        </div>
      </div>
    ))}
  </div>
);

const Table = ({
  head,
  children,
}: {
  head: ReactNode[];
  children: ReactNode;
}) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left text-xs text-muted-foreground">
          {head.map((h, i) => (
            <th
              key={i}
              className={cn("py-2 pr-3 font-medium", i > 0 ? "text-right" : "")}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y">{children}</tbody>
    </table>
  </div>
);

const Empty = ({ children }: { children: ReactNode }) => (
  <p className="text-sm text-muted-foreground">{children}</p>
);

const SourcesTable = ({ sources }: { sources: WebOverview["sources"] }) => {
  if (!sources.length) return <Empty>Sin visitas en este periodo.</Empty>;
  return (
    <Table
      head={[
        "Origen · campaña · anuncio",
        "Visitas",
        "Pulsan comprar",
        "Que me llamen",
        "Compras",
        "Conversión",
      ]}
    >
      {sources.map((s, i) => (
        <tr key={i}>
          <td className="py-2 pr-3">
            <span className="font-medium">{sourceLabel(s.source)}</span>
            {s.campaign || s.content ? (
              <span className="block text-xs text-muted-foreground">
                {[s.campaign, s.content].filter(Boolean).join(" · ")}
              </span>
            ) : null}
          </td>
          <td className="py-2 pr-3 text-right tabular-nums">{s.sessions}</td>
          <td className="py-2 pr-3 text-right tabular-nums">{s.checkout}</td>
          <td className="py-2 pr-3 text-right tabular-nums">{s.leads}</td>
          <td className="py-2 pr-3 text-right tabular-nums">{s.purchases}</td>
          <td className="py-2 pr-3 text-right tabular-nums">
            {pct(s.leads + s.purchases, s.sessions)}%
          </td>
        </tr>
      ))}
    </Table>
  );
};

const PagesTable = ({
  pages,
  heatKey,
  days,
}: {
  pages: WebOverview["pages"];
  heatKey?: string;
  days: number;
}) => {
  if (!pages.length) return <Empty>Sin visitas en este periodo.</Empty>;
  const open = (path: string, device: HeatDevice) =>
    heatKey &&
    window.open(heatmapUrl(path, device, heatKey, Math.max(7, days)), "_blank");
  return (
    <Table head={["Página", "Vistas", "Clics", "Bajan hasta", "Mapa de calor"]}>
      {pages.map((p) => (
        <tr key={p.path}>
          <td className="py-2 pr-3">
            <span className="font-medium">{pageLabel(p.path)}</span>
            <span className="block text-xs text-muted-foreground">
              {p.path}
            </span>
          </td>
          <td className="py-2 pr-3 text-right tabular-nums">{p.views}</td>
          <td className="py-2 pr-3 text-right tabular-nums">{p.clicks}</td>
          <td
            className="py-2 pr-3 text-right tabular-nums"
            title="Media de hasta dónde bajan · % que llega al 75% de la página"
          >
            {p.scroll == null ? "—" : `${p.scroll}%`}
            {p.scroll_75 != null ? (
              <span className="block text-xs text-muted-foreground">
                {p.scroll_75}% llega al final
              </span>
            ) : null}
          </td>
          <td className="py-2 text-right">
            <span className="inline-flex gap-1">
              <HeatButton
                icon={Smartphone}
                label="Móvil"
                onClick={() => open(p.path, "movil")}
                disabled={!heatKey}
              />
              <HeatButton
                icon={Flame}
                label="Escritorio"
                onClick={() => open(p.path, "escritorio")}
                disabled={!heatKey}
              />
            </span>
          </td>
        </tr>
      ))}
    </Table>
  );
};

const HeatButton = ({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
  >
    <Icon className="h-3 w-3" />
    {label}
  </button>
);

const ClicksTable = ({ clicks }: { clicks: WebOverview["top_clicks"] }) => {
  if (!clicks.length) return <Empty>Aún no hay clics en este periodo.</Empty>;
  const max = Math.max(...clicks.map((c) => c.clicks));
  return (
    <ul className="flex flex-col gap-2">
      {clicks.slice(0, 15).map((c, i) => (
        <li key={i} className="text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2">
              <MousePointerClick className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate font-medium">{c.label}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {pageLabel(c.path)}
              </span>
            </span>
            <span className="shrink-0 tabular-nums">
              <b>{c.clicks}</b>{" "}
              <span className="text-xs text-muted-foreground">
                ({c.sessions} personas)
              </span>
            </span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-[#eb6834]"
              style={{ width: `${(100 * c.clicks) / max}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};

const LiveList = ({ sessions }: { sessions: WebLiveSession[] }) => {
  if (!sessions.length) {
    return <Empty>Nadie en la web en los últimos 5 minutos.</Empty>;
  }
  return (
    <ul className="flex flex-col gap-2">
      {sessions.map((s) => (
        <li key={s.id} className="rounded-2xl border px-3 py-2 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{pageLabel(s.path)}</span>
            <span className="text-xs text-muted-foreground">
              {s.device === "mobile" ? "móvil" : (s.device ?? "")}
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            {sourceLabel(s.source)}
            {s.campaign ? ` · ${s.campaign}` : ""} · {s.pageviews}{" "}
            {s.pageviews === 1 ? "página" : "páginas"}
            {s.chat_messages ? ` · habla con la IA` : ""}
            {s.events.includes("InitiateCheckout")
              ? " · ha pulsado comprar"
              : ""}
            {s.events.includes("Lead") ? " · pidió llamada" : ""}
          </div>
        </li>
      ))}
    </ul>
  );
};
